// src/pages/Affiliate.tsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "components/Header";
import Footer from "components/Footer";

// フロントからは相対パスで /api を叩く（本番／ローカル共通）
const API = process.env.REACT_APP_API_BASE_URL || "";

/** サーバーから返ってくるリンク行 */
type AffiliateLink = {
  id: string;
  accountName: string;
  affLink: string;
  affiliateId?: string;
};

/** アカウント毎の登録ユーザー行（A案用・購入履歴ボタン付き） */
type AffiliateUserRow = {
  userId: number; // 数値ID（51000003 等）
  uid: string; // 内部用ユーザーID（J-51000003 等）
  userName: string; // 表示用ユーザー名（ポンポン 等）
  affiliateId: string;
  registeredAt: string;
  orderCount: number;
};

/** ワイヤー準拠：1カラム太線フォーム + 下段一覧 + 最下部CTA */
export default function Affiliate() {
  /* ===== state ===== */
  const [keyInput, setKeyInput] = useState<string>(() => {
    // 以前認証した鍵があれば初期値に入れておく
    try {
      return localStorage.getItem("affKey") || "";
    } catch {
      return "";
    }
  });
  const [verified, setVerified] = useState(false);
  const [affKey, setAffKey] = useState(""); // 認証済みキー保持

  const [accountName, setAccountName] = useState("");
  const [sourceLink, setSourceLink] = useState("");
  const [issuedLink, setIssuedLink] = useState(""); // 発行結果の表示

  // links = 登録アフィリンク一覧
  const [affiliates, setAffiliates] = useState<AffiliateLink[]>([]);

  // allUsers = すべての登録ユーザー（鍵単位）
  // users = 選択中アカウントに絞り込んだ一覧
  const [allUsers, setAllUsers] = useState<AffiliateUserRow[]>([]);
  const [users, setUsers] = useState<AffiliateUserRow[]>([]);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [selectedAffId, setSelectedAffId] = useState<string | null>(null);
  const [selectedAffName, setSelectedAffName] = useState<string>("");

  const nav = useNavigate();

  /* ===== helpers ===== */

  // 現在選択中のアフィリエイトアカウント
  const selectedAffiliate = selectedAffId
    ? affiliates.find((a) => a.id === selectedAffId) || null
    : null;

  // ユーザー一覧を「媒体者ID」でフィルタする
  const filterUsersByAffiliate = (
    rows: AffiliateUserRow[],
    affiliateId?: string | null
  ) => {
    const affId = affiliateId || "";
    if (!affId) return rows;
    return rows.filter(
      (u) => String(u.affiliateId || "") === String(affId)
    );
  };

  // A案用：鍵に紐づく「すべての登録ユーザー」を取得し、
  // 選択中アカウントに絞り込んで users にセット
  const fetchUsersByKey = async (
    key: string,
    initialAffiliateId?: string | null
  ) => {
    try {
      const res = await fetch(`${API}/api/affiliate/users`, {
        headers: { "x-aff-key": key },
        credentials: "include",
      });
      if (!res.ok) return;

      const json = await res.json();

      const rawUsers = Array.isArray(json?.users) ? json.users : [];

      // サーバーのフィールド名とフロントの型をここで正規化しておく
     // uid から J-51000003 → 51000003 を抜くヘルパー
const parseUserIdFromRow = (r: any): number => {
  if (r.userId != null) return Number(r.userId);
  if (r.id != null) return Number(r.id);

  const uidStr = String(r.uid ?? r.userCode ?? r.userId ?? r.id ?? "");
  const m = uidStr.match(/J-(\d+)/);
  if (m) return Number(m[1]);

  return 0;
};

const rows: AffiliateUserRow[] = rawUsers.map((r: any) => ({
  userId: parseUserIdFromRow(r),
  uid: String(
    r.uid ?? r.userCode ?? r.userId ?? r.id ?? ""
  ),
  userName: String(
    r.userName ?? r.lineName ?? r.name ?? ""
  ),
  affiliateId: String(r.affiliateId ?? r.affId ?? ""),
  registeredAt: String(
    r.registeredAt ?? r.date ?? r.createdAt ?? ""
  ),
  orderCount: Number(
    r.orderCount ?? r.purchases ?? r.purchaseCount ?? 0
  ),
}));


      setAllUsers(rows);

      const baseAffiliateId =
        initialAffiliateId ||
        selectedAffiliate?.affiliateId ||
        null;

      if (baseAffiliateId) {
        setUsers(filterUsersByAffiliate(rows, baseAffiliateId));
      } else {
        setUsers(rows);
      }
    } catch {
      /* noop */
    }
  };

  const reloadLists = async () => {
    const key = affKey || keyInput;
    if (!key) return;

    const currentAffiliateId = selectedAffiliate?.affiliateId || null;
    await fetchUsersByKey(key, currentAffiliateId);
  };

  /* ===== API ===== */

  // 認証（鍵 → グループ情報 & リンク一覧 & 登録ユーザー一覧）
  const handleVerify = async () => {
    setErr(null);
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/affiliate/verify-key`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key: keyInput }),
        credentials: "include",
      });
      if (!r.ok) throw new Error("認証に失敗しました");
      const data = await r.json();

      setVerified(true);
      setAffKey(keyInput);

      // サーバーから返ってきた links（= 登録アフィリンク一覧）を採用
      const links: AffiliateLink[] =
        Array.isArray((data as any).links) && (data as any).links.length
          ? (data as any).links
          : [];

      setAffiliates(links);

      // 認証に成功した鍵を永続保存
      try {
        localStorage.setItem("affKey", keyInput);
      } catch {
        // localStorage が使えない環境では無視
      }

      // 先頭行を選択状態にする（accounts の先頭）
      const first = links[0];
      if (first) {
        setSelectedAffId(String(first.id));
        setSelectedAffName(first.accountName || "");
      } else {
        setSelectedAffId(null);
        setSelectedAffName("");
      }

      // ユーザー一覧はキーから取得し、選択中 affiliateId で絞り込む
      const initialAffiliateId = first?.affiliateId || null;
      await fetchUsersByKey(keyInput, initialAffiliateId);
    } catch (e: any) {
      setErr(e?.message || "通信エラー");
    } finally {
      setLoading(false);
    }
  };

  /** 発行＆登録：リンク下の行に発行結果を表示する */
  const handleIssueAndRegister = async () => {
    setErr(null);
    if (!accountName || !sourceLink) {
      setErr("アカウント名とリンクを入力してください");
      return;
    }

    // affKey が未設定でも keyInput があればそれを使う
    const key = affKey || keyInput;
    if (!key) {
      setErr("先にキー認証を行ってください");
      return;
    }

    // 最小運用の発行URL（/entry?bom=トークン）
    const base = window.location.origin;
    const token = Math.random().toString(36).slice(2, 10);
    const aff = `${base}/entry?bom=${token}`;
    setIssuedLink(aff); // UIに即時反映

    setLoading(true);
    try {
      const r = await fetch(`${API}/api/affiliate/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // セッションクッキーを一緒に送る
        body: JSON.stringify({
          key,
          accountName,
          link: sourceLink,
          affLink: aff,
        }),
      });

      if (!r.ok) throw new Error("発行＆登録に失敗しました");
      const js = await r.json().catch(() => null);

      // 返ってきた「link レコード」を一覧に追加
      if (js?.link) {
        setAffiliates((prev) => [
          ...prev,
          js.link as AffiliateLink,
        ]);

        // サーバー側が affLink を返してくるので、それを優先表示
        if (js.link.affLink) {
          setIssuedLink(js.link.affLink);
        }

        // ついでに選択中の行も更新しておく
        setSelectedAffId(js.link.id);
        setSelectedAffName(js.link.accountName || "");
      }

      await reloadLists();
    } catch (e: any) {
      setErr(e?.message || "通信エラー");
    } finally {
      setLoading(false);
    }
  };

  // 「購入履歴」ボタン押下 → /affiliate/user-orders を叩いてアラート表示
  const handleShowUserOrders = async (user: AffiliateUserRow) => {
    setErr(null);

    const currentAff = selectedAffiliate;
    if (!currentAff || !currentAff.affiliateId) {
      alert("アカウントを選択してください。");
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        affiliateId: String(currentAff.affiliateId),
        userId: String(user.userId),
      });

      const res = await fetch(
        `${API}/api/affiliate/user-orders?${params.toString()}`,
        { credentials: "include" }
      );

      if (!res.ok) {
        throw new Error("購入履歴の取得に失敗しました");
      }

      const js = await res.json();
      const orders = Array.isArray(js?.orders) ? js.orders : [];

      const label = user.userName || user.uid;

      if (orders.length === 0) {
        alert(
          `ユーザー名: ${label}\nまだ購入履歴はありません。`
        );
        return;
      }

      const lines = orders.map((o: any) => {
        const date = String(
          o.date || o.purchasedAt || ""
        ).replace("T", " ");
        const plan = o.planName || o.plan || "ポイント購入";
        const amount = Number(
          o.amountJPY ?? o.priceJPY ?? o.amount ?? 0
        );
        const amountStr =
          amount > 0 ? `${amount.toLocaleString()}円` : "";
        return `${date}  ${plan}  ${amountStr}`;
      });

      alert(
        `ユーザー名: ${label} の購入履歴\n\n${lines.join("\n")}`
      );
    } catch (e: any) {
      setErr(
        e?.message ||
          "購入履歴の取得でエラーが発生しました"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ===== styles（太線の帳票風） ===== */
  const wrap: React.CSSProperties = {
    maxWidth: 720,
    margin: "0 auto",
    padding: "14px 10px 28px",
  };
  const panel: React.CSSProperties = {
    background: "rgba(255,255,255,.92)",
    borderRadius: 12,
    border: "1px solid #e5e9f2",
    padding: 12,
    marginBottom: 14,
  };
  const titleMain: React.CSSProperties = {
    fontWeight: 900,
    fontSize: 22,
    color: "#0b1a27",
    marginBottom: 10,
  };

  const box: React.CSSProperties = {
    border: "2px solid rgba(11,26,39,.85)",
    borderRadius: 6,
    overflow: "hidden",
  };
  const row: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "170px 1fr",
    alignItems: "stretch",
    borderTop: "2px solid rgba(11,26,39,.85)",
  };
  // 入力欄 + ボタンの2カラム（リンク行用）
  const row3: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "1fr 150px",
    alignItems: "stretch",
    borderTop: "2px solid rgba(11,26,39,.85)",
  };

  const lh: React.CSSProperties = {
    padding: "10px 12px",
    fontWeight: 800,
    background: "rgba(255,255,255,.9)",
  };
  const rh: React.CSSProperties = {
    padding: "6px 10px",
    background: "rgba(255,255,255,.9)",
  };
  const input: React.CSSProperties = {
    width: "100%",
    padding: "12px 8px",
    border: "none",
    outline: "none",
    fontSize: 16,
    background: "transparent",
  };
  const btn: React.CSSProperties = {
    width: "100%",
    height: "100%",
    border: "none",
    background: "#1e73be",
    color: "#fff",
    fontWeight: 900,
    fontSize: 16,
    cursor: "pointer",
  };

  const smallBtn: React.CSSProperties = {
    padding: "6px 10px",
    borderRadius: 4,
    border: "none",
    background: "#1e73be",
    color: "#fff",
    fontWeight: 800,
    fontSize: 13,
    cursor: "pointer",
    width: "100%",
  };

  const headRow: React.CSSProperties = {
    ...row,
    borderTop: "none",
    background: "#f7f9fc",
  };
  const th: React.CSSProperties = { ...lh, fontSize: 14 };
  const dataRow: React.CSSProperties = {
    ...row,
    background: "rgba(255,255,255,.95)",
  };
  const cell: React.CSSProperties = { ...rh, fontSize: 15 };

  const cta: React.CSSProperties = {
    width: "100%",
    border: "none",
    background: "#1e73be",
    color: "#fff",
    fontWeight: 900,
    fontSize: 22,
    letterSpacing: ".06em",
    padding: "18px 12px",
    borderRadius: 10,
    cursor: "pointer",
  };

  return (
    // 下付きフッター & body overflow対策
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "#f5f7fb",
        overflowY: "auto",
      }}
    >
      <Header />

      <main style={{ flex: 1 }}>
        <div style={wrap}>
          {/* タイトル & 鍵入力 */}
          <section style={panel}>
            <div style={titleMain}>メンテナンス</div>
            <div style={box}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 120px",
                }}
              >
                <input
                  style={{
                    ...input,
                    borderRight:
                      "2px solid rgba(11,26,39,.85)",
                  }}
                  placeholder="メンテナンスキーを入力"
                  value={keyInput}
                  onChange={(e) =>
                    setKeyInput(e.target.value)
                  }
                />
                <button
                  style={btn}
                  onClick={handleVerify}
                  disabled={loading}
                >
                  認証
                </button>
              </div>
            </div>
            {err && (
              <div
                style={{
                  color: "#c0392b",
                  fontWeight: 700,
                  marginTop: 8,
                  whiteSpace: "pre-line",
                }}
              >
                ⚠ {err}
              </div>
            )}
            {loading && (
              <div
                style={{
                  color: "#1e73be",
                  fontWeight: 700,
                  marginTop: 8,
                }}
              >
                通信中…
              </div>
            )}
          </section>

          {/* 認証後 */}
          {verified && (
            <>
              {/* フォーム（リンクの右が「発行＆登録」） */}
              <section style={panel}>
                <div style={box}>
                  {/* アカウント名（ラベルを背景に統合・枠全体タップで入力） */}
                  <div
                    style={{
                      ...row,
                      borderTop: "none",
                      gridTemplateColumns: "1fr",
                    }}
                  >
                    <div style={rh}>
                      <input
                        style={input}
                        value={accountName}
                        onChange={(e) =>
                          setAccountName(e.target.value)
                        }
                        placeholder="アカウント名"
                      />
                    </div>
                  </div>

                  {/* リンク + ボタン（ラベルを背景に統合） */}
                  <div style={row3}>
                    <div style={rh}>
                      <input
                        style={input}
                        placeholder="リンク（https://...）"
                        value={sourceLink}
                        onChange={(e) =>
                          setSourceLink(e.target.value)
                        }
                      />
                    </div>
                    <button
                      style={btn}
                      onClick={handleIssueAndRegister}
                      disabled={loading}
                    >
                      発行＆登録
                    </button>
                  </div>

                  {/* 発行結果を“下の行”に表示 */}
                  <div style={row}>
                    <div style={lh}>
                      発行アフィリエイトリンク
                    </div>
                    <div style={rh}>
                      <input
                        style={input}
                        value={issuedLink}
                        readOnly
                        placeholder="ここに発行リンクが表示されます"
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* 登録アフィリンク一覧 */}
              <section style={panel}>
                <div
                  style={{
                    fontWeight: 900,
                    fontSize: 20,
                    color: "#0b1a27",
                    margin: "8px 0",
                  }}
                >
                  登録アフィリンク一覧
                </div>
                <div style={box}>
                  {/* ヘッダー行（アカウント名 → アフィリンク の順） */}
                  <div
                    style={{
                      ...headRow,
                      gridTemplateColumns: "1fr 180px",
                    }}
                  >
                    <div style={th}>アカウント名</div>
                    <div
                      style={{
                        ...th,
                        textAlign: "left",
                        borderLeft:
                          "2px solid rgba(11,26,39,.85)",
                      }}
                    >
                      アフィリエイトリンク
                    </div>
                  </div>

                  {/* データ行 */}
                  {affiliates.length === 0 ? (
                    <div
                      style={{
                        ...dataRow,
                        gridTemplateColumns: "1fr 180px",
                      }}
                    >
                      <div
                        style={{
                          ...cell,
                          gridColumn: "1 / span 2",
                        }}
                      >
                        まだ登録がありません
                      </div>
                    </div>
                  ) : (
                    affiliates.map((a) => {
                      const isSel =
                        selectedAffId === a.id;
                      return (
                        <div
                          key={a.id}
                          onClick={() => {
                            setSelectedAffId(a.id);
                            setSelectedAffName(
                              a.accountName || ""
                            );
                            setUsers(
                              filterUsersByAffiliate(
                                allUsers,
                                a.affiliateId
                              )
                            );
                          }}
                          style={{
                            ...dataRow,
                            gridTemplateColumns:
                              "1fr 180px",
                            cursor: "pointer",
                            background: isSel
                              ? "rgba(30,115,190,0.08)"
                              : (dataRow as any)
                                  .background,
                            outline: isSel
                              ? "2px solid rgba(30,115,190,0.35)"
                              : "none",
                          }}
                          title="クリックで選択"
                        >
                          {/* 1列目：アカウント名 */}
                          <div
                            style={{
                              ...cell,
                              fontWeight: isSel
                                ? 800
                                : 700,
                            }}
                          >
                            {a.accountName}
                          </div>
                          {/* 2列目：アフィリエイトリンク */}
                          <div
                            style={{
                              ...cell,
                              textAlign: "left",
                              borderLeft:
                                "2px solid rgba(11,26,39,.85)",
                            }}
                          >
                            <a
                              href={a.affLink}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {a.affLink.replace(
                                "?aff=",
                                "?bom="
                              )}
                            </a>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </section>

              {/* アカウント毎の登録ユーザー */}
              <section style={panel}>
                <div
                  style={{
                    fontWeight: 900,
                    fontSize: 20,
                    color: "#0b1a27",
                    margin: "8px 0",
                  }}
                >
                  アカウント毎の登録ユーザー
                  {selectedAffName ? (
                    <span
                      style={{
                        fontWeight: 800,
                        marginLeft: 8,
                        color: "#1e73be",
                      }}
                    >
                      （選択中: {selectedAffName}）
                    </span>
                  ) : null}
                </div>

                <div style={box}>
                  <div
                    style={{
                      ...headRow,
                      gridTemplateColumns:
                        "170px 1fr 140px",
                    }}
                  >
                    <div style={th}>ユーザー名</div>
                    <div style={th}>登録日</div>
                    <div style={th}>購入履歴</div>
                  </div>
                  {users.length === 0 ? (
                    <div
                      style={{
                        ...dataRow,
                        gridTemplateColumns:
                          "170px 1fr 140px",
                      }}
                    >
                      <div
                        style={{
                          ...cell,
                          gridColumn: "1 / span 3",
                        }}
                      >
                        まだ登録ユーザーがいません
                      </div>
                    </div>
                  ) : (
                    users.map((u) => (
                      <div
                        style={{
                          ...dataRow,
                          gridTemplateColumns:
                            "170px 1fr 140px",
                        }}
                        key={`${u.userId}_${u.uid}`}
                      >
                        <div style={cell}>
                          {u.userName || u.uid}
                        </div>
                        <div style={cell}>
                          {u.registeredAt}
                        </div>
                        <div style={cell}>
                          <button
                            style={smallBtn}
                            onClick={() =>
                              handleShowUserOrders(u)
                            }
                          >
                            履歴を見る
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </section>

              {/* 最下部：売上確認（青帯・幅いっぱい） */}
              <section style={panel}>
                <button
                  style={cta}
                  onClick={() => {
                    try {
                      nav("/affiliate/sales");
                    } catch {
                      alert(
                        "売上確認ページは準備中です"
                      );
                    }
                  }}
                >
                  売上を確認する
                </button>
              </section>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
